import React, { useState, useEffect } from 'react';

// Notification Types
const NOTIFICATION_TYPES = {
  FOLLOW: 'follow',
  LIKE: 'like', 
  REPOST: 'repost',
  REPLY: 'reply',
  MENTION: 'mention'
};

// Individual Notification Component
const NotificationItem = ({ notification, onMarkRead, onUserClick }) => {
  const getNotificationIcon = () => {
    switch (notification.type) {
      case NOTIFICATION_TYPES.FOLLOW:
        return (
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        );
      case NOTIFICATION_TYPES.LIKE:
        return (
          <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
        );
      case NOTIFICATION_TYPES.REPOST:
        return (
          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        );
      case NOTIFICATION_TYPES.REPLY:
        return (
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        );
    }
  };

  const getNotificationText = () => {
    // Support both formats: actor_name or actor.name
    const actorName = notification.actor_name || notification.actor?.name || 'Cineva';
    
    switch (notification.type) {
      case NOTIFICATION_TYPES.FOLLOW:
        return `${actorName} a început să te urmărească`;
      case NOTIFICATION_TYPES.LIKE:
        return `${actorName} a apreciat postarea ta`;
      case NOTIFICATION_TYPES.REPOST:
        return `${actorName} a redistribuit postarea ta`;
      case NOTIFICATION_TYPES.REPLY:
        return `${actorName} a răspuns la postarea ta`;
      case NOTIFICATION_TYPES.MENTION:
        return `${actorName} te-a menționat într-o postare`;
      default:
        return notification.content || 'Notificare nouă';
    }
  };

  const timeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return 'acum';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    return `${Math.floor(diffInSeconds / 86400)}d`;
  };

  return (
    <div 
      className={`flex items-start space-x-3 p-4 border-b border-gray-800/50 hover:bg-gray-800/20 cursor-pointer transition-colors duration-200 ${
        !notification.read ? 'bg-blue-500/5 border-l-4 border-l-blue-500' : ''
      }`}
      onClick={() => {
        if (onMarkRead) onMarkRead(notification.id);
        if (onUserClick && notification.actor_id) onUserClick(notification.actor_id);
      }}
    >
      {getNotificationIcon()}
      
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm">
          {getNotificationText()}
        </p>
        
        {notification.post_content && (
          <p className="text-gray-400 text-sm mt-1 truncate">
            "{notification.post_content}"
          </p>
        )}
        
        <p className="text-gray-500 text-xs mt-1">
          {timeAgo(notification.created_at)}
        </p>
      </div>
      
      {!notification.read && (
        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-2"></div>
      )}
    </div>
  );
};

// Notifications Panel Component
export const NotificationsPanel = ({ 
  notifications = [], 
  isOpen, 
  onClose, 
  onMarkRead, 
  onMarkAllRead,
  onUserClick 
}) => {
  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-16">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md max-h-[80vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <h2 className="text-xl font-bold text-white">Notificări</h2>
          <div className="flex items-center space-x-3">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllRead}
                className="text-blue-500 hover:text-blue-400 text-sm font-medium"
              >
                Marchează toate ca citite
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-gray-400 text-lg">Nicio notificare</p>
              <p className="text-gray-500 text-sm mt-1">
                Vei primi notificări când cineva interacționează cu postările tale
              </p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={onMarkRead}
                  onUserClick={onUserClick}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Notification Badge Component - Always shows bell, badge only when count > 0
export const NotificationBadge = ({ count = 0, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-full hover:bg-gray-700/50 transition-colors duration-200"
      aria-label={count > 0 ? `${count} notificări necitite` : 'Notificări'}
      data-testid="notification-bell"
    >
      <svg className="w-6 h-6 text-gray-300 hover:text-white transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      
      {count > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

export { NOTIFICATION_TYPES };
export default NotificationItem;
